import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from './Localization'; // Import the custom hook

const Navbar = ({ games }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, toggleLanguage, language } = useLanguage();

  // Switch the navbar from transparent (over the hero) to solid on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '#features', label: t('highlights') },
    { href: '#games', label: t('ourGames') },
    { href: '#footer', label: t('contact') },
  ];

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled || isMenuOpen
          ? 'bg-gray-900/90 backdrop-blur-md shadow-lg shadow-black/10'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div
          className={`flex items-center justify-between transition-all duration-300 ${
            scrolled ? 'h-16' : 'h-20'
          }`}
        >
          <a href="/" className="flex items-center group">
            <img
              src="/assets/img/logo/logo_site.png"
              alt={t('altText')}
              className={`transition-all duration-300 group-hover:scale-105 ${
                scrolled ? 'h-14' : 'h-20'
              }`}
            />
          </a>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-10">
            {links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="relative text-base font-bold text-white/90 hover:text-brand-300 transition-colors duration-200 after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:rounded-full after:bg-brand-400 after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
              </a>
            ))}
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              aria-label="Toggle language"
              className="ml-4 rounded-full ring-1 ring-white/30 p-0.5 hover:ring-brand-400 hover:scale-110 transition-all duration-200"
            >
              <img
                src={language === 'en' ? '/assets/img/FR.png' : '/assets/img/GB.png'}
                alt={language === 'en' ? 'FR' : 'EN'}
                className="w-6 h-6 rounded-full object-cover"
              />
            </button>
          </div>

          {/* Mobile controls */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={toggleLanguage}
              aria-label="Toggle language"
              className="rounded-full ring-1 ring-white/30 p-0.5"
            >
              <img
                src={language === 'en' ? '/assets/img/FR.png' : '/assets/img/GB.png'}
                alt={language === 'en' ? 'FR' : 'EN'}
                className="w-6 h-6 rounded-full object-cover"
              />
            </button>
            <button
              className="p-2 text-white"
              aria-label="Toggle menu"
              onClick={() => setIsMenuOpen((v) => !v)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="md:hidden overflow-hidden"
            >
              <div className="py-3 flex flex-col">
                {links.map((link, i) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    onClick={closeMenu}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    className="block py-3 px-2 text-white font-semibold border-b border-white/10 hover:text-brand-300 hover:pl-4 transition-all duration-200"
                  >
                    {link.label}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
